import { useTranslation } from 'react-i18next'
import { Card, CardContent, Badge } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'
import { useChannelNameLookup } from '@/hooks/useChannels'
import type { SalesChannelRef } from './masterData.types'

function resolve(ids: string[], lookup: Record<string, string>): SalesChannelRef[] {
  return ids.map((rawId): SalesChannelRef => {
    const name = lookup[rawId]
    if (!name) return { rawId, name: null, status: 'unsynced' }
    if (name === rawId) return { rawId, name: null, status: 'unnamed' }
    return { rawId, name, status: 'named' }
  })
}

export function SalesChannelsCard({ ids }: { ids: string[] }) {
  const { t } = useTranslation()
  const lookup = useChannelNameLookup()
  const refs = resolve(ids, lookup)
  return (
    <Card id={EID['masterdata-card-channels']}>
      <CardContent className="p-6">
        <h3 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {t('productMasterData.cards.salesChannels')}
          <Badge variant="secondary">{ids.length}</Badge>
        </h3>
        <ul className="space-y-1.5">
          {refs.map((r) => (
            <li key={r.rawId} className="flex items-center gap-2 text-sm">
              <span className={`h-1.5 w-1.5 rounded-full ${r.status === 'named' ? 'bg-primary-500' : 'bg-neutral-400'}`} />
              {r.status === 'named' ? (
                <span className="text-neutral-900 dark:text-neutral-100">{r.name}</span>
              ) : (
                <>
                  <span className="text-neutral-500 dark:text-neutral-400">
                    {t(r.status === 'unnamed' ? 'productMasterData.channelUnnamed' : 'productMasterData.channelUnsynced')}
                  </span>
                  <code className="text-xs text-neutral-400">{r.rawId}</code>
                </>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
